import { memo } from 'react'

import type { InfirmierPlanning, InterventionPlanning } from '../../lib/api/types'
import { formatHeure, initiales } from '../../lib/formatters'

interface Proprietes {
  intervention: InterventionPlanning
  infirmier: InfirmierPlanning | undefined
  ligneDebut: number
  nbLignes: number
  colonne: number
  deborde: boolean
  /** Décalage horizontal quand plusieurs interventions se chevauchent. */
  indexChevauchement: number
  nbChevauchements: number
  selectionnee: boolean
  interactive: boolean
  onOuvrir: (intervention: InterventionPlanning) => void
}

/**
 * Une case du planning.
 *
 * `React.memo` avec comparaison sur `id` et `updated_at` : avec 300 blocs à
 * l'écran, re-rendre l'ensemble à chaque frappe dans un filtre est ce qui fait
 * ramer la page. `updated_at` suffit comme empreinte — c'est précisément
 * pourquoi le format verrouillé de `/planning/semaine` l'inclut.
 */
function CarteInterventionInterne({
  intervention,
  infirmier,
  ligneDebut,
  nbLignes,
  colonne,
  deborde,
  indexChevauchement,
  nbChevauchements,
  selectionnee,
  interactive,
  onOuvrir,
}: Proprietes) {
  const nonAffectee = intervention.utilisateur_id === null
  const annulee = intervention.statut === 'ANNULEE'
  const realisee = intervention.statut === 'REALISEE'

  // La couleur n'est JAMAIS le seul porteur d'information (contraste AA,
  // daltonisme) : l'initiale de l'infirmier l'accompagne toujours, et une
  // intervention non affectée porte un liseré en tirets reconnaissable sans
  // distinguer les teintes.
  const couleur = infirmier?.couleur ?? '#94a3b8'

  const largeur = nbChevauchements > 1 ? `${100 / nbChevauchements}%` : '100%'
  const decalage = nbChevauchements > 1 ? `${(indexChevauchement * 100) / nbChevauchements}%` : '0'

  return (
    <div
      style={{
        gridColumn: colonne,
        gridRow: `${ligneDebut} / span ${nbLignes}`,
        width: largeur,
        marginLeft: decalage,
      }}
      className="relative z-10 p-px"
    >
      <button
        type="button"
        draggable={interactive}
        onDragStart={(evenement) => {
          // Le glisser natif transporte l'identifiant : suffisant ici, et sans
          // la dependance @dnd-kit — qui serait un ecart au contrat.
          evenement.dataTransfer.setData('text/plain', intervention.id)
          evenement.dataTransfer.effectAllowed = 'move'
        }}
        onClick={() => onOuvrir(intervention)}
        aria-label={`${intervention.patient_prenom} ${intervention.patient_nom}, ${formatHeure(
          intervention.heure_debut,
        )} à ${formatHeure(intervention.heure_fin)}, ${
          intervention.soins.map((s) => s.libelle).join(', ') || 'aucun soin'
        }, ${nonAffectee ? 'non affectée' : `avec ${infirmier?.prenom ?? ''} ${infirmier?.nom ?? ''}`}${
          intervention.conflits.length > 0 ? `, conflit : ${intervention.conflits.join(', ')}` : ''
        }`}
        className={`flex h-full w-full flex-col overflow-hidden rounded border-l-4 px-1.5 py-0.5 text-left text-[11px] leading-tight transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-1 ${
          nonAffectee
            ? 'border-dashed border-slate-400 bg-slate-100 hover:bg-slate-200'
            : 'hover:brightness-95'
        } ${annulee ? 'opacity-50 line-through' : ''} ${selectionnee ? 'ring-2 ring-sky-600' : ''}`}
        style={
          nonAffectee
            ? undefined
            : { borderLeftColor: couleur, backgroundColor: `${couleur}1a` }
        }
      >
        <span className="flex items-center gap-1 font-medium text-slate-900">
          <span className="tabular-nums">{formatHeure(intervention.heure_debut)}</span>
          {nonAffectee ? (
            <span className="rounded bg-slate-300 px-1 text-[9px] font-bold text-slate-700">?</span>
          ) : (
            <span
              className="rounded px-1 text-[9px] font-bold text-white"
              style={{ backgroundColor: couleur }}
            >
              {infirmier ? initiales(infirmier) : '??'}
            </span>
          )}
          {realisee ? <span aria-hidden="true">✓</span> : null}
          {intervention.conflits.length > 0 ? (
            <span title={intervention.conflits.join(', ')} aria-hidden="true">
              ⚠
            </span>
          ) : null}
          {deborde ? (
            <span title="Déborde de l’amplitude affichée" aria-hidden="true">
              ↕
            </span>
          ) : null}
        </span>

        <span className="truncate text-slate-800">
          {intervention.patient_nom} {intervention.patient_prenom.charAt(0)}.
        </span>

        {nbLignes > 1 && intervention.soins.length > 0 ? (
          <span className="mt-0.5 flex flex-wrap gap-0.5">
            {intervention.soins.map((s) => (
              <span
                key={s.type_soin_id}
                className="rounded px-1 text-[9px] font-medium text-white"
                style={{ backgroundColor: s.couleur }}
              >
                {s.code}
              </span>
            ))}
          </span>
        ) : null}
      </button>
    </div>
  )
}

export const CarteIntervention = memo(
  CarteInterventionInterne,
  (avant, apres) =>
    avant.intervention.id === apres.intervention.id &&
    avant.intervention.updated_at === apres.intervention.updated_at &&
    avant.selectionnee === apres.selectionnee &&
    avant.ligneDebut === apres.ligneDebut &&
    avant.nbLignes === apres.nbLignes &&
    avant.colonne === apres.colonne &&
    avant.indexChevauchement === apres.indexChevauchement &&
    avant.nbChevauchements === apres.nbChevauchements &&
    avant.interactive === apres.interactive,
)
